import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AutomationType } from '@playbook-technologies/common-enums';
import { useState } from 'react';

import type { Async } from '@client/new/helpers/types';

import { InsightsCopy, copyFor } from '../automation-copy';
import { INVALID_EMAIL_MESSAGE, isEmailAddress } from '../email-address';
import {
  AutomationLogEntry,
  AutomationStatsEntry,
  AutomationStepInsight,
} from '../automations.api';
import { AutomationLogsFilter } from '../automations.keys';
import {
  AutomationInsightsError,
  useAutomationLogsModel,
  useAutomationStatsModel,
} from '../model/AutomationInsights.model';
import {
  AutomationsRequestErrorKind,
  useAutomationsModel,
} from '../model/Automations.model';

const INSIGHTS_ERROR_MESSAGES: Record<AutomationsRequestErrorKind, string> = {
  unauthorized: 'Your session has expired. Sign in again to view automations.',
  forbidden: "You don't have permission to view automation data.",
  network: "We couldn't load automation data.",
};

type InsightsViewModelError = {
  message: string;
  retry: () => Promise<void>;
};

const toViewModelError = (
  error: AutomationInsightsError,
): InsightsViewModelError => ({
  message: INSIGHTS_ERROR_MESSAGES[error.kind],
  retry: error.retry,
});

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

export type FunnelRow = {
  key: string;
  label: string;
  /** Second line under the label, e.g. "154 had no marketing consent". */
  note?: string;
  count: string;
  /** Relative to the first row, 0..1, drives the bar width. */
  share: number;
  /** Percentage of the first row; absent on the first row itself. */
  percent?: string;
  color: 'primary' | 'success';
};

export type FunnelCard = {
  automationType: string;
  displayName: string;
  icon: IconProp;
  headline: { value: string; label: string } | null;
  rows: FunnelRow[];
  /** Admin-only detail kept from the pilot stats card. */
  details: {
    closedTotal: string;
    closeReasons: { key: string; label: string; count: string }[];
    drainingLine: string | null;
    steps: {
      key: string;
      label: string;
      sent: string;
      opens: string;
      clicks: string;
    }[];
  };
};

export type NotRunningCard = {
  automationType: string;
  displayName: string;
  description: string;
  icon: IconProp;
  needsSetup: boolean;
};

export type InsightsDrawerViewModel = {
  /** Sum of `converted` over the running automations. */
  totalConversions: string;
  totalCaption: string;
  running: FunnelCard[];
  notRunning: NotRunningCard[];
  adminTools: boolean;
};

const totalCaptionFor = (running: number) =>
  `Across ${running} ${
    running === 1 ? 'automation' : 'automations'
  } running, all time. A conversion is a member who got one of these messages and then did what it asked for: bought, subscribed or switched plan.`;

const integer = new Intl.NumberFormat('en-US');
const formatCount = (n: number): string => integer.format(n);
const formatShare = (part: number, whole: number): number =>
  whole > 0 ? Math.min(1, Math.max(0, part / whole)) : 0;
const formatPercent = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : '0%';

const toDrainingLine = (entry: AutomationStatsEntry): string | null => {
  const draining = (entry.versions ?? [])
    .filter((version) => !version.isCurrent)
    .reduce((sum, version) => sum + version.active, 0);
  return draining > 0 ? `Previous version, draining: ${draining}` : null;
};

const isEmailStep = (step: AutomationStepInsight) =>
  step.channel === undefined || step.channel === 'email';

/**
 * Stats are per step, the Figma funnel is per person. "Emails sent" is the
 * first email step (everyone reached at least once); opened and clicked take
 * the best email step, a lower bound for "opened any email" that never
 * exceeds the people reached. Push steps carry no opens or clicks.
 */
export const toFunnelRows = (
  entry: AutomationStatsEntry,
  copy: InsightsCopy,
): FunnelRow[] => {
  const emailSteps = entry.steps.filter(isEmailStep);
  const reached = emailSteps[0]?.sent ?? 0;
  const opened = Math.max(0, ...emailSteps.map((s) => s.uniqueOpens));
  const clicked = Math.max(0, ...emailSteps.map((s) => s.uniqueClicks));
  const noConsent = entry.discrepancy.reasons.find(
    (reason) => reason.key === 'no_consent',
  );
  const base = entry.enrolled;
  const row = (
    key: string,
    label: string,
    value: number,
    color: FunnelRow['color'] = 'primary',
    note?: string,
  ): FunnelRow => ({
    key,
    label,
    note,
    count: formatCount(value),
    share: formatShare(value, base),
    percent: key === 'enrolled' ? undefined : formatPercent(value, base),
    color,
  });
  const rows = [
    row('enrolled', copy.enrolledLabel, base),
    row(
      'sent',
      'Emails sent',
      reached,
      'primary',
      noConsent && noConsent.count > 0
        ? `${noConsent.count} had no marketing consent`
        : undefined,
    ),
    row('opened', 'Opened', opened),
    row('clicked', 'Clicked', clicked),
  ];
  return copy.convertedLabel === null
    ? rows
    : [
        ...rows,
        row('converted', copy.convertedLabel, entry.converted, 'success'),
      ];
};

export const toHeadline = (
  entry: AutomationStatsEntry,
  copy: InsightsCopy,
): FunnelCard['headline'] =>
  copy.convertedLabel === null
    ? null
    : { value: formatCount(entry.converted), label: copy.convertedLabel };

const toFunnelCard = (
  entry: AutomationStatsEntry,
  displayName: string,
): FunnelCard => {
  const copy = copyFor(entry.automationType);
  return {
    automationType: entry.automationType,
    displayName,
    icon: copy.icon,
    headline: toHeadline(entry, copy.insights),
    rows: toFunnelRows(entry, copy.insights),
    details: {
      closedTotal: formatCount(entry.discrepancy.total),
      closeReasons: entry.discrepancy.reasons.map((reason) => ({
        key: reason.key,
        label: reason.label,
        count: formatCount(reason.count),
      })),
      drainingLine: toDrainingLine(entry),
      steps: entry.steps.map((step) => ({
        key: step.key,
        label: step.label,
        sent: formatCount(step.sent),
        opens: formatCount(step.uniqueOpens),
        clicks: formatCount(step.uniqueClicks),
      })),
    },
  };
};

/** Orchestrates the stats and the list: stats carry the numbers, the list
 *  says which automations are on (Running) and which are not. */
export const useInsightsDrawerViewModel = (
  creatorId: number,
): Async<InsightsDrawerViewModel, InsightsViewModelError> => {
  const stats = useAutomationStatsModel(creatorId);
  const list = useAutomationsModel(creatorId);

  if (stats.type === 'fetch-error') {
    return { type: 'fetch-error', value: toViewModelError(stats.value) };
  }
  if (list.type === 'fetch-error') {
    return {
      type: 'fetch-error',
      value: {
        message: INSIGHTS_ERROR_MESSAGES.network,
        retry: list.value.retry,
      },
    };
  }
  if (stats.type !== 'fetched') return stats;
  if (list.type !== 'fetched') return list;

  const statsByType = new Map(
    stats.value.stats.map((entry) => [entry.automationType, entry]),
  );
  const running: FunnelCard[] = [];
  const notRunning: NotRunningCard[] = [];
  let totalConversions = 0;
  for (const item of list.value.automations) {
    const entry = statsByType.get(item.automationType);
    if (item.enabled && entry) {
      running.push(toFunnelCard(entry, item.displayName));
      totalConversions += entry.converted;
    } else {
      notRunning.push({
        automationType: item.automationType,
        displayName: item.displayName,
        description: item.description,
        icon: copyFor(item.automationType).icon,
        needsSetup: item.status === 'needs_setup',
      });
    }
  }

  return {
    type: 'fetched',
    value: {
      totalConversions: formatCount(totalConversions),
      totalCaption: totalCaptionFor(running.length),
      running,
      notRunning,
      adminTools: list.value.adminTools,
    },
  };
};

export type LogStepRow = {
  stepNo: number;
  sentAt: string;
  openedAt: string;
  clickedAt: string;
};

export type LogStatus = {
  label: string;
  /** Keyed on the server's closeReason, not on the humanised label. */
  kind: 'active' | 'goal' | 'closed';
};

export type LogRow = {
  id: string;
  email: string;
  trigger: string;
  progress: string;
  status: LogStatus;
  firstSentAt: string;
  steps: LogStepRow[];
};

/** No `status` on the wire: open logs have `closedAt === null`, closed ones
 * carry the outcome in `closeReason` (a server key, humanized here). */
const toStatus = (entry: AutomationLogEntry): LogStatus => {
  if (entry.closedAt === null) return { label: 'Active', kind: 'active' };
  const reason = entry.closeReason;
  return {
    label: reason ? reason.replace(/_/g, ' ') : 'Closed',
    kind: reason?.startsWith('goal') ? 'goal' : 'closed',
  };
};

const toLogRow = (entry: AutomationLogEntry): LogRow => ({
  id: entry.id,
  email: entry.email,
  trigger: entry.triggerKey,
  progress: `${entry.completedStepCount}/${entry.stepCount}`,
  status: toStatus(entry),
  firstSentAt: formatDateTime(entry.steps[0]?.sentAt ?? null),
  steps: entry.steps.map((step) => ({
    stepNo: step.stepNo,
    sentAt: formatDateTime(step.sentAt),
    openedAt: formatDateTime(step.openedAt),
    clickedAt: formatDateTime(step.clickedAt),
  })),
});

type LogsViewModel = {
  rows: LogRow[];
  total: number;
  isRefreshing: boolean;
  /** The table failed to load; the filters stay usable so the user can fix the input (GRW-819). */
  loadError?: InsightsViewModelError;
  page: number;
  perPage: number;
  emailInput: string;
  /** Inline validation for "Email (exact)"; set instead of sending a non-email to the API (GRW-819). */
  emailError?: string;
  automationType: AutomationType | '';
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setEmailInput: (value: string) => void;
  applyEmailFilter: () => void;
  /** One click: empties the field and re-runs the unfiltered search (GRW-824). */
  clearEmailFilter: () => void;
  setAutomationType: (automationType: AutomationType | '') => void;
};

export const PER_PAGE_OPTIONS = [20, 50, 100] as const;

export const useAutomationLogsViewModel = (
  creatorId: number,
): Async<LogsViewModel, InsightsViewModelError> => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_OPTIONS[0]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [appliedEmail, setAppliedEmail] = useState('');
  const [automationType, setAutomationType] = useState<AutomationType | ''>('');

  const filter: AutomationLogsFilter = {
    ...(automationType ? { automationType } : {}),
    ...(appliedEmail ? { email: appliedEmail } : {}),
    page: page + 1,
    perPage,
  };
  const model = useAutomationLogsModel(creatorId, filter);

  if (model.type === 'not-fetched' || model.type === 'fetching') return model;

  const table =
    model.type === 'fetched'
      ? {
          rows: model.value.entries.map(toLogRow),
          total: model.value.total,
          isRefreshing: model.value.isRefreshing,
        }
      : {
          rows: [],
          total: 0,
          isRefreshing: false,
          loadError: toViewModelError(model.value),
        };

  return {
    type: 'fetched',
    value: {
      ...table,
      page,
      perPage,
      emailInput,
      emailError,
      automationType,
      setPage,
      setPerPage: (next) => {
        setPerPage(next);
        setPage(0);
      },
      setEmailInput,
      applyEmailFilter: () => {
        const trimmed = emailInput.trim();
        if (trimmed && !isEmailAddress(trimmed)) {
          setEmailError(INVALID_EMAIL_MESSAGE);
          return;
        }
        setEmailError(undefined);
        setAppliedEmail(trimmed);
        setPage(0);
      },
      clearEmailFilter: () => {
        setEmailInput('');
        setEmailError(undefined);
        setAppliedEmail('');
        setPage(0);
      },
      setAutomationType: (next) => {
        setAutomationType(next);
        setPage(0);
      },
    },
  };
};
